/**
 * DropdownNav Component - Accessible
 *
 * IMPROVEMENTS:
 * - Added full ARIA attributes for screen readers
 * - Keyboard navigation support (Arrow keys, Escape, Enter)
 * - Focus management (trap focus, return focus)
 * - Proper semantic HTML with role="menu"
 * - Uses shared hooks for better code reusability
 */

import React, { useState, useRef, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useClickOutside } from "../hooks/useClickOutside";
import { useNavigationState } from "../hooks/useNavigationState";
import { useKeyboardNavigation } from "../hooks/useKeyboardNavigation";
import { DropdownNavProps } from "../types/navigation";
import styles from "../styles/DropdownNav.module.css";

export default function DropdownNav({ label, items }: DropdownNavProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1); // -1 = no focus indicator shown; 0+ indicates keyboard-focused item
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const navigate = useNavigate();

    // Use shared navigation state hook
    const { isActiveSection } = useNavigationState(items);

    // Click outside to close
    useClickOutside(dropdownRef, () => setIsOpen(false), isOpen);

    // Keyboard navigation
    const handleKeyDown = useKeyboardNavigation({
        isOpen,
        itemCount: items.length,
        onNavigate: (index) => {
            navigate(items[index].to);
            setIsOpen(false);
        },
        onClose: () => {
            setIsOpen(false);
            buttonRef.current?.focus();
        },
        focusedIndex,
        setFocusedIndex,
    });

    const handleMouseEnter = useCallback(() => {
        // Only enable hover on desktop (>1064px) - medium screens use click
        if (window.matchMedia("(min-width: 1065px)").matches) {
            setIsOpen(true);
            setFocusedIndex(0);
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        // Only enable hover on desktop (>1064px) - medium screens use click
        if (window.matchMedia("(min-width: 1065px)").matches) {
            setIsOpen(false);
            setFocusedIndex(0);
        }
    }, []);

    const handleItemClick = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleLabelClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        // On desktop (>1065px), navigate to first item. On medium screens, just toggle dropdown.
        const isDesktop = window.matchMedia("(min-width: 1065px)").matches;
        if (isDesktop && items.length > 0) {
            navigate(items[0].to);
        } else {
            setIsOpen((prev) => !prev);
        }
    }, [navigate, items]);

    return (
        <div
            className={styles.dropdown}
            ref={dropdownRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onKeyDown={handleKeyDown}
        >
            <button
                ref={buttonRef}
                className={`${styles.dropdownTrigger} ${isOpen ? styles.open : ""} ${isActiveSection ? styles.active : ""}`}
                onClick={handleLabelClick}
                aria-expanded={isOpen}
                aria-haspopup="true"
                aria-controls={`${label.toLowerCase()}-dropdown-menu`}
                aria-label={`${label} menu`}
            >
                {label}
                <span className={styles.dropdownArrow} aria-hidden="true">
                    ▾
                </span>
            </button>

            {isOpen && (
                <div
                    id={`${label.toLowerCase()}-dropdown-menu`}
                    className={styles.dropdownMenu}
                    role="menu"
                    aria-label={`${label} submenu`}
                >
                    {items.map((item, index) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end
                            role="menuitem"
                            tabIndex={index === focusedIndex ? 0 : -1}
                            className={({ isActive }) =>
                                `${styles.dropdownItem} ${isActive ? styles.active : ""}`
                            }
                            onClick={handleItemClick}
                            onMouseEnter={() => setFocusedIndex(index)}
                            onMouseLeave={() => setFocusedIndex(0)}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}
